import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { useCreateAtivo } from '@/hooks/useAtivos';
import { useFabricantes } from '@/hooks/useFabricantes';
import { useClients } from '@/hooks/useClients';
import { useContratos } from '@/hooks/useContratos';
import { useTiposAtivos } from '@/hooks/useTiposAtivos';
import { useLocalizacoes } from '@/hooks/useLocalizacoes';
import { useUsers } from '@/hooks/useUsers';
import { useCamposTipoAtivo } from '@/hooks/useCamposTipoAtivo';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { 
  BulkImportResult, 
  ImportValidationError, 
  ImportOptions, 
  RelationshipMapping 
} from '@/types/bulk-import';
import { AtivoFormData } from '@/types/ativo';
import { RelationshipType, ImportanceLevel } from '@/types/asset-relationship';

const VALID_RELATIONSHIP_TYPES: RelationshipType[] = [
  'contains', 'depends_on', 'connects_to', 'hosts', 'uses',
  'manages', 'monitors', 'backup_of', 'replaces', 'supports', 'part_of'
];

const VALID_IMPORTANCE_LEVELS: ImportanceLevel[] = ['critical', 'high', 'medium', 'low'];

export const useBulkImportAtivos = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const createAtivo = useCreateAtivo();
  const { fabricantes } = useFabricantes();
  const { clients } = useClients();
  const { contratos } = useContratos();
  const { data: tiposAtivos } = useTiposAtivos();
  const { localizacoes } = useLocalizacoes();
  const { users } = useUsers();

  const [isProcessing, setIsProcessing] = useState(false);

  const csvHeaders = [
    'nome',
    'cliente',
    'tipo',
    'descricao',
    'status_operacional',
    'ambiente',
    'localizacao',
    'fabricante',
    'modelo',
    'numero_serie',
    'patrimonio',
    'sistema_operacional',
    'versao_firmware',
    'data_aquisicao',
    'data_instalacao',
    'data_garantia_inicio',
    'data_garantia_fim',
    'valor_aquisicao',
    'centro_de_custo',
    'proprietario',
    'contrato',
    'relacionamento_destino',
    'relacionamento_tipo',
    'relacionamento_importancia',
    'relacionamento_descricao'
  ];

  const downloadTemplate = (tipoAtivoId?: string) => {
    let headers = [...csvHeaders];
    
    if (tipoAtivoId) {
      // placeholder for custom fields
    }
    
    const csvContent = [
      headers.join(','),
      '"Servidor Web","Cliente Exemplo","Servidor","Servidor de aplicação web","ativo","producao","Datacenter Principal","Dell","PowerEdge R740","ABC123","PAT001","Ubuntu 20.04","1.0.0","2024-01-15","2024-01-20","2024-01-15","2027-01-15","15000","TI-001","João Silva","CONT-001","Switch Core","depends_on","high","Depende do switch para conectividade"',
      '"Switch Core","Cliente Exemplo","Switch","Switch principal do datacenter","ativo","producao","Datacenter Principal","Cisco","Catalyst 9300","SW-001","PAT002","","","2024-01-10","2024-01-12","2024-01-10","2027-01-10","25000","TI-001","","","",""'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_importacao_ativos.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildRelationshipMappings = (): RelationshipMapping => {
    const mappings: RelationshipMapping = {
      fabricantes: new Map(),
      clientes: new Map(),
      usuarios: new Map(),
      contratos: new Map(),
      localizacoes: new Map(),
      tipos: new Map()
    };

    fabricantes?.forEach(fab => {
      mappings.fabricantes.set(fab.nome.toLowerCase(), fab.id);
    });

    clients?.forEach(client => {
      mappings.clientes.set(client.name.toLowerCase(), client.id);
    });

    contratos?.forEach(contrato => {
      mappings.contratos.set(contrato.numero_contrato.toLowerCase(), contrato.id);
    });

    tiposAtivos?.forEach(tipo => {
      mappings.tipos.set(tipo.nome.toLowerCase(), tipo.id);
    });

    localizacoes?.forEach(localizacao => {
      mappings.localizacoes.set(localizacao.nome.toLowerCase(), localizacao.id);
    });

    users?.forEach(user => {
      mappings.usuarios.set(user.name.toLowerCase(), user.id);
    });

    return mappings;
  };

  const validateImportData = async (data: any[]): Promise<{ errors: ImportValidationError[] }> => {
    const errors: ImportValidationError[] = [];
    const mappings = buildRelationshipMappings();
    const allAssetNames = new Set(data.map(r => r.nome?.trim().toLowerCase()).filter(Boolean));

    // Also fetch existing assets for relationship target validation
    const { data: existingAssets } = await supabase
      .from('cmdb_ativos')
      .select('nome')
      .limit(5000);
    
    existingAssets?.forEach(a => allAssetNames.add(a.nome.toLowerCase()));

    data.forEach((row, index) => {
      const rowIndex = row._rowIndex || index + 2;

      if (!row.nome?.trim()) {
        errors.push({ row: rowIndex, field: 'nome', message: 'Nome é obrigatório', value: row.nome });
      }

      if (!row.cliente?.trim()) {
        errors.push({ row: rowIndex, field: 'cliente', message: 'Cliente é obrigatório', value: row.cliente });
      }

      if (row.cliente && !mappings.clientes.has(row.cliente.toLowerCase())) {
        errors.push({ row: rowIndex, field: 'cliente', message: `Cliente "${row.cliente}" não encontrado`, value: row.cliente });
      }

      if (row.fabricante && !mappings.fabricantes.has(row.fabricante.toLowerCase())) {
        errors.push({ row: rowIndex, field: 'fabricante', message: `Fabricante "${row.fabricante}" não encontrado`, value: row.fabricante });
      }

      if (row.tipo && !mappings.tipos.has(row.tipo.toLowerCase())) {
        errors.push({ row: rowIndex, field: 'tipo', message: `Tipo de ativo "${row.tipo}" não encontrado`, value: row.tipo });
      }

      if (row.localizacao && !mappings.localizacoes.has(row.localizacao.toLowerCase())) {
        errors.push({ row: rowIndex, field: 'localizacao', message: `Localização "${row.localizacao}" não encontrada`, value: row.localizacao });
      }

      if (row.proprietario && !mappings.usuarios.has(row.proprietario.toLowerCase())) {
        errors.push({ row: rowIndex, field: 'proprietario', message: `Proprietário "${row.proprietario}" não encontrado`, value: row.proprietario });
      }

      if (row.data_aquisicao && !isValidDate(row.data_aquisicao)) {
        errors.push({ row: rowIndex, field: 'data_aquisicao', message: 'Data de aquisição inválida (use formato YYYY-MM-DD)', value: row.data_aquisicao });
      }

      if (row.valor_aquisicao && isNaN(parseFloat(row.valor_aquisicao))) {
        errors.push({ row: rowIndex, field: 'valor_aquisicao', message: 'Valor de aquisição deve ser um número', value: row.valor_aquisicao });
      }

      // Validate relationship fields
      if (row.relacionamento_destino?.trim()) {
        if (!allAssetNames.has(row.relacionamento_destino.trim().toLowerCase())) {
          errors.push({ row: rowIndex, field: 'relacionamento_destino', message: `Ativo destino "${row.relacionamento_destino}" não encontrado no CSV nem no sistema`, value: row.relacionamento_destino });
        }

        if (!row.relacionamento_tipo?.trim()) {
          errors.push({ row: rowIndex, field: 'relacionamento_tipo', message: 'Tipo de relacionamento é obrigatório quando destino é informado', value: row.relacionamento_tipo });
        } else if (!VALID_RELATIONSHIP_TYPES.includes(row.relacionamento_tipo.trim() as RelationshipType)) {
          errors.push({ row: rowIndex, field: 'relacionamento_tipo', message: `Tipo "${row.relacionamento_tipo}" inválido. Use: ${VALID_RELATIONSHIP_TYPES.join(', ')}`, value: row.relacionamento_tipo });
        }

        if (row.relacionamento_importancia?.trim() && !VALID_IMPORTANCE_LEVELS.includes(row.relacionamento_importancia.trim() as ImportanceLevel)) {
          errors.push({ row: rowIndex, field: 'relacionamento_importancia', message: `Importância "${row.relacionamento_importancia}" inválida. Use: ${VALID_IMPORTANCE_LEVELS.join(', ')}`, value: row.relacionamento_importancia });
        }

        if (row.relacionamento_destino.trim().toLowerCase() === row.nome?.trim().toLowerCase()) {
          errors.push({ row: rowIndex, field: 'relacionamento_destino', message: 'Um ativo não pode ter relacionamento consigo mesmo', value: row.relacionamento_destino });
        }
      }
    });

    return { errors };
  };

  const isValidDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const processImport = async (
    data: any[], 
    options: ImportOptions
  ): Promise<BulkImportResult> => {
    setIsProcessing(true);
    
    const result: BulkImportResult = {
      successful: 0,
      failed: 0,
      created: 0,
      updated: 0,
      errors: [],
      summary: ''
    };

    try {
      const mappings = buildRelationshipMappings();
      const batchSize = options.batchSize || 10;
      const createdAssetMap = new Map<string, string>(); // name -> id
      const relationshipsToCreate: Array<{
        sourceNome: string;
        targetNome: string;
        type: RelationshipType;
        importance: ImportanceLevel;
        description?: string;
        clientId: string;
        rowIndex: number;
      }> = [];

      // Phase 1: Create assets
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        
        for (const row of batch) {
          try {
            const ativoData = await mapRowToAtivoData(row, mappings, options);
            const created = await createAtivo.mutateAsync(ativoData);
            result.successful++;
            result.created++;

            if (created?.id && row.nome) {
              createdAssetMap.set(row.nome.trim().toLowerCase(), created.id);
            }

            // Collect relationship data
            if (row.relacionamento_destino?.trim()) {
              const clienteId = mappings.clientes.get(row.cliente?.toLowerCase());
              if (clienteId) {
                relationshipsToCreate.push({
                  sourceNome: row.nome.trim(),
                  targetNome: row.relacionamento_destino.trim(),
                  type: row.relacionamento_tipo?.trim() as RelationshipType,
                  importance: (row.relacionamento_importancia?.trim() || 'medium') as ImportanceLevel,
                  description: row.relacionamento_descricao?.trim(),
                  clientId: clienteId,
                  rowIndex: row._rowIndex || i + 2,
                });
              }
            }
          } catch (error: any) {
            result.failed++;
            result.errors.push({
              row: row._rowIndex || i + 2,
              field: 'general',
              message: error.message || 'Erro desconhecido',
              value: row.nome
            });
          }
        }

        const progress = Math.round(((i + batch.length) / data.length) * 70);
        options.onProgress?.(progress);
      }

      // Phase 2: Create relationships
      if (relationshipsToCreate.length > 0) {
        // Fetch existing assets to resolve names
        const { data: existingAssets } = await supabase
          .from('cmdb_ativos')
          .select('id, nome')
          .limit(5000);
        
        existingAssets?.forEach(a => {
          if (!createdAssetMap.has(a.nome.toLowerCase())) {
            createdAssetMap.set(a.nome.toLowerCase(), a.id);
          }
        });

        let relProcessed = 0;
        for (const rel of relationshipsToCreate) {
          try {
            const sourceId = createdAssetMap.get(rel.sourceNome.toLowerCase());
            const targetId = createdAssetMap.get(rel.targetNome.toLowerCase());

            if (!sourceId) {
              throw new Error(`Ativo origem "${rel.sourceNome}" não encontrado`);
            }
            if (!targetId) {
              throw new Error(`Ativo destino "${rel.targetNome}" não encontrado`);
            }

            const { error } = await supabase
              .from('asset_relationships')
              .insert({
                source_asset_id: sourceId,
                target_asset_id: targetId,
                relationship_type: rel.type,
                importance_level: rel.importance,
                description: rel.description || null,
                client_id: rel.clientId,
                created_by: user?.id || '',
              });

            if (error) throw error;
            result.updated++; // reuse 'updated' count for relationships created
          } catch (error: any) {
            result.errors.push({
              row: rel.rowIndex,
              field: 'relacionamento',
              message: `Erro ao criar relacionamento: ${error.message}`,
              value: `${rel.sourceNome} → ${rel.targetNome}`
            });
          }
          relProcessed++;
          const relProgress = 70 + Math.round((relProcessed / relationshipsToCreate.length) * 30);
          options.onProgress?.(relProgress);
        }
      }

      options.onProgress?.(100);
      result.summary = `Importação concluída: ${result.successful} ativos criados, ${result.updated} relacionamentos criados, ${result.failed} falhas`;
      
      queryClient.invalidateQueries({ queryKey: ['ativos'] });
      queryClient.invalidateQueries({ queryKey: ['asset-relationships'] });

      toast({
        title: "Importação Concluída",
        description: result.summary,
        variant: result.failed > 0 || result.errors.length > 0 ? "destructive" : "default"
      });

    } catch (error: any) {
      console.error('Erro na importação:', error);
      toast({
        title: "Erro na Importação",
        description: "Falha ao processar a importação em lote",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }

    return result;
  };

  const mapRowToAtivoData = async (
    row: any, 
    mappings: RelationshipMapping, 
    options: ImportOptions
  ): Promise<AtivoFormData> => {
    const ativoData: AtivoFormData = {
      nome: row.nome?.trim(),
      descricao: row.descricao?.trim(),
      status_operacional: row.status_operacional?.trim() || 'ativo',
      ambiente: row.ambiente?.trim(),
      modelo: row.modelo?.trim(),
      numero_serie: row.numero_serie?.trim(),
      patrimonio: row.patrimonio?.trim(),
      sistema_operacional: row.sistema_operacional?.trim(),
      versao_firmware: row.versao_firmware?.trim(),
      data_aquisicao: row.data_aquisicao?.trim(),
      data_instalacao: row.data_instalacao?.trim(),
      data_garantia_inicio: row.data_garantia_inicio?.trim(),
      data_garantia_fim: row.data_garantia_fim?.trim(),
      valor_aquisicao: row.valor_aquisicao ? parseFloat(row.valor_aquisicao) : undefined,
      centro_de_custo: row.centro_de_custo?.trim()
    };

    if (row.cliente) {
      const clienteId = mappings.clientes.get(row.cliente.toLowerCase());
      if (clienteId) {
        ativoData.client_id = clienteId;
      } else if (options.createMissingRecords) {
        throw new Error(`Cliente "${row.cliente}" não encontrado`);
      }
    }

    if (row.fabricante) {
      const fabricanteId = mappings.fabricantes.get(row.fabricante.toLowerCase());
      if (fabricanteId) ativoData.fabricante_id = fabricanteId;
    }

    if (row.tipo) {
      const tipoId = mappings.tipos.get(row.tipo.toLowerCase());
      if (tipoId) ativoData.tipo_id = tipoId;
    }

    if (row.localizacao) {
      const localizacaoId = mappings.localizacoes.get(row.localizacao.toLowerCase());
      if (localizacaoId) ativoData.localizacao_id = localizacaoId;
    }

    if (row.proprietario) {
      const proprietarioId = mappings.usuarios.get(row.proprietario.toLowerCase());
      if (proprietarioId) ativoData.proprietario_id = proprietarioId;
    }

    // Custom fields
    const relationshipFields = ['relacionamento_destino', 'relacionamento_tipo', 'relacionamento_importancia', 'relacionamento_descricao'];
    const atributosCustomizados: Record<string, any> = {};
    Object.keys(row).forEach(key => {
      if (!csvHeaders.includes(key) && !relationshipFields.includes(key) && row[key] !== undefined && row[key] !== '') {
        atributosCustomizados[key] = row[key];
      }
    });
    
    if (Object.keys(atributosCustomizados).length > 0) {
      ativoData.atributos_customizados = atributosCustomizados;
    }

    return ativoData;
  };

  return {
    downloadTemplate,
    validateImportData,
    processImport,
    isProcessing
  };
};
